import { useEffect, useState } from 'react';
import AuthGuard from '../components/AuthGuard';
import { useNavigate } from 'react-router-dom';
import InnerLayout from '../components/InnerLayout';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

interface AddressType {
    id: number;
    name: string;
    phone: string;
    province: string | null;
    city: string | null;
    district: string | null;
    address: string;
    postal_code: string | null;
    is_default: boolean;
    full_address: string;
}

interface AddressForm {
    name: string;
    phone: string;
    province: string;
    city: string;
    district: string;
    address: string;
    postal_code: string;
    is_default: boolean;
}

const initialForm: AddressForm = {
    name: '',
    phone: '',
    province: '',
    city: '',
    district: '',
    address: '',
    postal_code: '',
    is_default: false,
};

export default function AddressPage() {
    const navigate = useNavigate();
    const [addresses, setAddresses] = useState<AddressType[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<AddressForm>(initialForm);
    const [submitting, setSubmitting] = useState(false);

    async function loadAddresses() {
        try {
            setLoading(true);
            const { data } = await api.get('/addresses');
            setAddresses(data?.addresses || []);
        } catch {
            toast.error('加载地址失败');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadAddresses();
    }, []);

    function openAddModal() {
        setEditingId(null);
        setForm(initialForm);
        setShowModal(true);
    }

    function openEditModal(addr: AddressType) {
        setEditingId(addr.id);
        setForm({
            name: addr.name,
            phone: addr.phone,
            province: addr.province || '',
            city: addr.city || '',
            district: addr.district || '',
            address: addr.address,
            postal_code: addr.postal_code || '',
            is_default: addr.is_default,
        });
        setShowModal(true);
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (submitting) return;
        if (!form.name.trim()) { toast.error('请输入收件人姓名'); return; }
        if (!form.phone.trim()) { toast.error('请输入联系电话'); return; }
        if (!form.address.trim()) { toast.error('请输入详细地址'); return; }

        try {
            setSubmitting(true);
            if (editingId) {
                await api.put(`/addresses/${editingId}`, form);
                toast.success('地址更新成功');
            } else {
                await api.post('/addresses', form);
                toast.success('地址添加成功');
            }
            setShowModal(false);
            loadAddresses();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || '操作失败');
        } finally {
            setSubmitting(false);
        }
    }

    async function handleDelete(id: number) {
        if (!confirm('确定要删除这个地址吗？')) return;
        try {
            await api.delete(`/addresses/${id}`);
            toast.success('地址已删除');
            loadAddresses();
        } catch {
            toast.error('删除失败');
        }
    }

    async function handleSetDefault(id: number) {
        try {
            await api.post(`/addresses/${id}/default`);
            toast.success('已设为默认地址');
            loadAddresses();
        } catch {
            toast.error('设置失败');
        }
    }

    return (
        <div
            className="max-w-md mx-auto min-h-screen bg-[#8b0000]"
        >
            <AuthGuard />

            {/* Header */}
            <div className="relative pt-4 px-4">
                <button
                    onClick={() => navigate(-1)}
                    className="absolute top-4 left-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 text-black"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                </button>
                <div className="flex flex-col items-center gap-3 justify-center relative text-white">
                    <h4>我的收货地址</h4>
                </div>
            </div>

            <div className="max-w-md mx-auto px-4 py-6">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                    <button
                        onClick={openAddModal}
                        className="px-4 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors"
                    >
                        + 添加地址
                    </button>
                </div>

                {/* Address List */}
                {loading ? (
                    <div className="text-center py-10 text-gray-500">加载中...</div>
                ) : addresses.length === 0 ? (
                    <div className="text-center py-10 text-gray-500">
                        <div className="text-4xl mb-3">📍</div>
                        <div>暂无收货地址</div>
                        <button
                            onClick={openAddModal}
                            className="mt-4 px-6 py-2 bg-red-500 text-white text-sm rounded-lg"
                        >
                            添加地址
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {addresses.map((addr) => (
                            <div
                                key={addr.id}
                                className={`bg-white rounded-lg p-4 shadow-sm border-2 ${addr.is_default ? 'border-red-500' : 'border-transparent'}`}
                            >
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-medium">{addr.name}</span>
                                            <span className="text-gray-500">{addr.phone}</span>
                                            {addr.is_default && (
                                                <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded">默认</span>
                                            )}
                                        </div>
                                        <div className="text-sm text-gray-600">{addr.full_address}</div>
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
                                    {!addr.is_default && (
                                        <button
                                            onClick={() => handleSetDefault(addr.id)}
                                            className="px-3 py-1 text-xs text-gray-600 hover:text-red-500"
                                        >
                                            设为默认
                                        </button>
                                    )}
                                    <button
                                        onClick={() => openEditModal(addr)}
                                        className="px-3 py-1 text-xs text-blue-600 hover:text-blue-700"
                                    >
                                        编辑
                                    </button>
                                    <button
                                        onClick={() => handleDelete(addr.id)}
                                        className="px-3 py-1 text-xs text-red-500 hover:text-red-600"
                                    >
                                        删除
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Modal */}
                {showModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowModal(false)}>
                        <div className="bg-white rounded-xl p-5 w-[90%] max-w-md max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                            <h2 className="text-lg font-bold mb-4">{editingId ? '编辑地址' : '添加地址'}</h2>
                            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                                <div>
                                    <label className="block text-sm text-gray-600 mb-1">收件人 *</label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border rounded-lg text-sm"
                                        placeholder="请输入收件人姓名"
                                        value={form.name}
                                        onChange={e => setForm({ ...form, name: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-600 mb-1">联系电话 *</label>
                                    <input
                                        type="tel"
                                        className="w-full px-3 py-2 border rounded-lg text-sm"
                                        placeholder="请输入联系电话"
                                        value={form.phone}
                                        onChange={e => setForm({ ...form, phone: e.target.value })}
                                    />
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <label className="block text-sm text-gray-600 mb-1">省份</label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 border rounded-lg text-sm"
                                            placeholder="省份"
                                            value={form.province}
                                            onChange={e => setForm({ ...form, province: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-600 mb-1">城市</label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 border rounded-lg text-sm"
                                            placeholder="城市"
                                            value={form.city}
                                            onChange={e => setForm({ ...form, city: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-600 mb-1">区县</label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 border rounded-lg text-sm"
                                            placeholder="区县"
                                            value={form.district}
                                            onChange={e => setForm({ ...form, district: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-600 mb-1">详细地址 *</label>
                                    <textarea
                                        className="w-full px-3 py-2 border rounded-lg text-sm min-h-[80px]"
                                        placeholder="请输入详细地址（街道、门牌号等）"
                                        value={form.address}
                                        onChange={e => setForm({ ...form, address: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-600 mb-1">邮编</label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border rounded-lg text-sm"
                                        placeholder="邮政编码（选填）"
                                        value={form.postal_code}
                                        onChange={e => setForm({ ...form, postal_code: e.target.value })}
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="is_default"
                                        checked={form.is_default}
                                        onChange={e => setForm({ ...form, is_default: e.target.checked })}
                                        className="w-4 h-4 accent-red-500"
                                    />
                                    <label htmlFor="is_default" className="text-sm">设为默认地址</label>
                                </div>
                                <div className="flex gap-3 mt-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowModal(false)}
                                        className="flex-1 py-2 border rounded-lg text-sm"
                                    >
                                        取消
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex-1 py-2 bg-red-500 text-white rounded-lg text-sm disabled:opacity-50"
                                    >
                                        {submitting ? '保存中...' : '保存'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
